import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import './ui.css';

interface ModalProps {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children?: ReactNode;
  footer?: ReactNode;
  /**
   * `wide` for a dialog holding a data table rather than a form.
   *
   * Declared here rather than inferred from the content with `:has()`, as the
   * form dialogs are: a table dialog renders no table while it is loading,
   * empty or failed, so a content-keyed rule would collapse the dialog back to
   * the narrow width in exactly the states where that looks broken.
   */
  size?: 'default' | 'wide';
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Dialog with Escape-to-close and focus kept inside while open.
 */
export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  size = 'default',
}: ModalProps) {
  const { t } = useTranslation('admin');
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  const focusables = useCallback(
    (): HTMLElement[] =>
      Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []),
    [],
  );

  // `onClose` is an inline arrow at most call sites, so depending on it would
  // re-run the effect on every parent render: the trigger would be recaptured
  // as a node inside the dialog and focus would be yanked back to the close
  // button mid-interaction. The effect must depend on `open` alone.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    const trigger = document.activeElement as HTMLElement | null;
    focusables()[0]?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    /* The shell scrolls its content region rather than the document, so locking
       the body is not enough on its own: a wheel over the backdrop would chain
       into that region and scroll the page behind the dialog. Saved and restored
       rather than deleted, so closing a nested dialog does not unlock the one
       still open underneath it. */
    const previousModalFlag = document.body.dataset.modalOpen;
    document.body.dataset.modalOpen = 'true';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Stops other listeners on `document` too, so only the topmost dialog
        // reacts; stopPropagation alone would not.
        event.stopImmediatePropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const items = focusables();
      if (items.length === 0) {
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previousModalFlag == null) {
        delete document.body.dataset.modalOpen;
      } else {
        document.body.dataset.modalOpen = previousModalFlag;
      }
      trigger?.focus();
    };
  }, [focusables, open]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="ui-modal-backdrop"
      onMouseDown={event => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}>
      <div
        ref={dialogRef}
        className={`ui-modal${size === 'wide' ? ' ui-modal--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}>
        <div className="ui-modal__header">
          <div>
            <h2 id={titleId} className="ui-modal__title">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="ui-modal__description">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="ui-modal__close"
            onClick={onClose}
            aria-label={t('common.close')}>
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        {children ? <div className="ui-modal__body">{children}</div> : null}
        {footer ? <div className="ui-modal__footer">{footer}</div> : null}
      </div>
    </div>
  );
}
